import { useEffect, useRef } from 'react'

import { useCopy, useGoTo, useLang } from '../../i18n'
import { CtaPair } from '../PlayStory'
import { Pit } from '../pit'
import { C, Shape } from '../shapes'

const STEP_SHAPES = [
  ['tri', C.yellow],
  ['square', C.green],
  ['circle', C.pink],
  ['half', C.blue]
]

/**
 * Abschluss: großer Aufruf mit beiden CTAs, darunter ein Feld aus Aivy-Formen,
 * die man anschubsen und per Klick nachwerfen kann.
 */
export default function PlStart() {
  const c = useCopy('start')
  const ui = useCopy('ui')
  const goTo = useGoTo()
  const lang = useLang()
  const rootRef = useRef(null)
  const canvasRef = useRef(null)

  useEffect(() => {
    const root = rootRef.current
    const canvas = canvasRef.current
    if (!root || !canvas) return undefined
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const pit = new Pit(canvas)
    let raf = 0
    let last = 0
    let visible = false

    const loop = (now) => {
      const dt = Math.min(1 / 30, (now - last) / 1000 || 1 / 60)
      last = now
      pit.step(dt / 2)
      pit.step(dt / 2)
      pit.render()
      raf = visible ? requestAnimationFrame(loop) : 0
    }
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
      if (visible) {
        pit.fill(pit.w < 720 ? 22 : 44)
        if (reduce) {
          // Ohne Bewegung: Formen direkt liegend zeichnen
          for (let i = 0; i < 400; i++) pit.step(1 / 60)
          pit.render()
          return
        }
        if (!raf) {
          last = performance.now()
          raf = requestAnimationFrame(loop)
        }
      }
    })
    io.observe(root)

    const move = (e) => {
      const r = canvas.getBoundingClientRect()
      pit.point(e.clientX - r.left, e.clientY - r.top)
    }
    const away = () => pit.point(-9999, -9999)
    const down = (e) => {
      if (e.target.closest('a, button')) return
      const r = canvas.getBoundingClientRect()
      for (let i = 0; i < 5; i++)
        pit.spawn(e.clientX - r.left, e.clientY - r.top, true)
    }
    const resize = () => pit.resize()
    root.addEventListener('pointermove', move)
    root.addEventListener('pointerleave', away)
    root.addEventListener('pointerdown', down)
    window.addEventListener('resize', resize)
    return () => {
      cancelAnimationFrame(raf)
      io.disconnect()
      root.removeEventListener('pointermove', move)
      root.removeEventListener('pointerleave', away)
      root.removeEventListener('pointerdown', down)
      window.removeEventListener('resize', resize)
    }
  }, [])

  const candidate = `${window.location.pathname}?v=bewerbende-showcase-hell${lang === 'en' ? '&lang=en' : ''}`

  return (
    <div ref={rootRef} className='ap-start'>
      <canvas ref={canvasRef} className='ap-start__pit' aria-hidden='true' />
      <div className='ap-start__content'>
        <div className='ap-eyebrow'>{c.eyebrow}</div>
        <h2 className='ap-display'>
          {c.title.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </h2>
        <p className='ap-lead'>{c.sub}</p>
        <CtaPair ui={ui} size='xl' />
        <ol className='ap-start__steps'>
          {c.steps.map((s, i) => (
            <li key={s}>
              <Shape
                kind={STEP_SHAPES[i][0]}
                color={STEP_SHAPES[i][1]}
                size={18}
              />
              {s}
            </li>
          ))}
        </ol>
        <div className='ap-start__links'>
          <a href={candidate}>{c.candidate}</a>
          <button type='button' onClick={() => goTo('hero')}>
            {c.again}
          </button>
        </div>
      </div>
      <div className='ap-start__poke' aria-hidden='true'>
        {c.poke}
      </div>
    </div>
  )
}
